import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, AtSign, Loader2, Eye, EyeOff, Check, X, Coffee } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

// Common weak passwords to block
const COMMON_PASSWORDS = [
  "123456", "password", "12345678", "qwerty", "123456789", 
  "12345", "1234", "111111", "1234567", "dragon", 
  "123123", "baseball", "iloveyou", "trustno1", "sunshine",
  "password1", "password123", "welcome", "shadow", "ashley"
];

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [acceptedKVKK, setAcceptedKVKK] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);

  // Password validation rules
  const passwordRules = useMemo(() => {
    const password = formData.password;
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      notCommon: !COMMON_PASSWORDS.includes(password.toLowerCase()),
    };
  }, [formData.password]);

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    const rules = Object.values(passwordRules);
    const passedRules = rules.filter(Boolean).length;
    
    if (formData.password.length === 0) return { level: 0, label: "", color: "" };
    if (passedRules <= 2) return { level: 1, label: "Zayıf", color: "bg-red-500" };
    if (passedRules <= 4) return { level: 2, label: "Orta", color: "bg-yellow-500" };
    return { level: 3, label: "Güçlü", color: "bg-green-500" };
  }, [passwordRules, formData.password]);

  // Check if passwords match
  const passwordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;

  // Check if all required rules pass
  const allRulesPassed = Object.values(passwordRules).every(Boolean);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!allRulesPassed) {
      toast.error("Şifre tüm güvenlik kurallarını karşılamalı");
      return;
    }

    if (!passwordsMatch) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }

    if (!acceptedKVKK) {
      toast.error("KVKK Aydınlatma Metni'ni kabul etmelisiniz");
      return;
    }

    setLoading(true);

    try {
      const response = await api.register({
        name: formData.name,
        username: formData.username,
        email: formData.email,
        password: formData.password,
        accepted_kvkk: acceptedKVKK,
        accepted_marketing: acceptedMarketing,
      });
      login(response.data.user, response.data.token);
      toast.success("Kayıt başarılı! Hoş geldiniz!");
      navigate("/onboarding");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const RuleItem = ({ passed, text }) => (
    <div className={`flex items-center gap-2 text-xs ${passed ? "text-green-400" : "text-[#64748B]"}`}>
      {passed ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      <span>{text}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex flex-col items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_game-tracker-92/artifacts/62gbiyum_WhatsApp%20Image%202026-01-30%20at%2002.16.12.jpeg" 
              alt="Kahve LİGİ Logo"
              className="w-28 h-28 rounded-2xl object-contain"
            />
            <span className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider gold-gradient-text">
              Kahve LİGİ
            </span>
          </Link>
        </div>

        {/* Motivational Quote */}
        <div className="text-center mb-6 p-3 rounded-lg bg-[#C6A87C]/10 border border-[#C6A87C]/20">
          <p className="text-sm text-[#C6A87C] italic flex items-center justify-center gap-2">
            <Coffee className="w-4 h-4" />
            "Masada güçlü olmanın yolu, hesabını da güçlü tutmaktan geçer."
          </p>
        </div>

        <div className="glass-card p-6">
          <h2 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white text-center mb-6">
            Kayıt Ol
          </h2>

          {/* Google Register */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full bg-white text-gray-800 hover:bg-gray-100 mb-6"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google ile Kayıt Ol
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-[#0A1635] px-2 text-[#64748B]">veya</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-[#B1C1C1]">Ad Soyad</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Adınız Soyadınız"
                  className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-11"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            {/* Username */}
            <div>
              <Label htmlFor="username" className="text-[#B1C1C1]">Kullanıcı Adı</Label>
              <div className="relative mt-1">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="kullanici_adi"
                  className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-11"
                  pattern="^[a-zA-Z0-9_]+$"
                  required
                  data-testid="username-input"
                />
              </div>
              <p className="text-xs text-[#64748B] mt-1">Sadece harf, rakam ve alt çizgi kullanılabilir</p>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-[#B1C1C1]">E-posta</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ornek@email.com"
                  className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-11"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password" className="text-[#B1C1C1]">Şifre</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-[#050E2E]/50 border-white/10 text-white h-11"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {formData.password.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.level / 3) * 100}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.level === 1 ? "text-red-400" :
                      passwordStrength.level === 2 ? "text-yellow-400" :
                      passwordStrength.level === 3 ? "text-green-400" : ""
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>

                  {/* Password Rules */}
                  <div className="grid grid-cols-2 gap-1 p-2 rounded-lg bg-white/5">
                    <RuleItem passed={passwordRules.minLength} text="En az 8 karakter" />
                    <RuleItem passed={passwordRules.hasUppercase} text="Büyük harf (A-Z)" />
                    <RuleItem passed={passwordRules.hasLowercase} text="Küçük harf (a-z)" />
                    <RuleItem passed={passwordRules.hasNumber} text="Rakam (0-9)" />
                    <RuleItem passed={passwordRules.hasSpecial} text="Özel karakter (!@#$)" />
                    <RuleItem passed={passwordRules.notCommon} text="Yaygın şifre değil" />
                  </div>

                  {passwordStrength.level < 3 && (
                    <p className="text-xs text-[#C6A87C] mt-2">
                      💡 Daha güçlü bir şifre için özel karakter ve rakam ekleyebilirsin.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword" className="text-[#B1C1C1]">Şifre Tekrar</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={`pl-10 pr-10 bg-[#050E2E]/50 border-white/10 text-white h-11 ${
                    formData.confirmPassword.length > 0 && !passwordsMatch ? "border-red-500" : ""
                  }`}
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                  <X className="w-3 h-3" />
                  Şifreler eşleşmiyor
                </p>
              )}
              {passwordsMatch && (
                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Şifreler eşleşiyor
                </p>
              )}
            </div>

            {/* KVKK Consent */}
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="kvkk"
                  checked={acceptedKVKK}
                  onCheckedChange={setAcceptedKVKK}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-[#4DFFFF] data-[state=checked]:border-[#4DFFFF]"
                  data-testid="kvkk-checkbox"
                />
                <label htmlFor="kvkk" className="text-xs text-[#B1C1C1] cursor-pointer">
                  <Link to="/kvkk" className="text-[#4DFFFF] hover:underline" target="_blank">
                    Kişisel Verilerin Korunması ve İşlenmesine İlişkin Aydınlatma Metni
                  </Link>'ni okudum ve kabul ediyorum. <span className="text-red-400">*</span>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="marketing"
                  checked={acceptedMarketing}
                  onCheckedChange={setAcceptedMarketing}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-[#4DFFFF] data-[state=checked]:border-[#4DFFFF]"
                  data-testid="marketing-checkbox"
                />
                <label htmlFor="marketing" className="text-xs text-[#B1C1C1] cursor-pointer">
                  Kampanya, turnuva ve lig bilgilendirmeleri hakkında e-posta almak istiyorum.
                </label>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading || !allRulesPassed || !passwordsMatch || !acceptedKVKK}
              className="w-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold h-11 mt-4"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Kayıt yapılıyor...
                </>
              ) : (
                "Kayıt Ol"
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-[#64748B] mt-6">
            Zaten hesabın var mı?{" "}
            <Link to="/login" className="text-[#4DFFFF] hover:underline">
              Giriş Yap
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
